// client/src/components/people/ui.js
// Small building blocks shared by every people page: the Individuals and
// RGA Partners tables, the person modal and form, and the profile page.
import { useState, useMemo } from 'react'

export const CONTACT_TYPE_BADGE = {
  'Investor':        'badge-blue',
  'GP':              'badge-green',
  'RGA Partner':     'badge-purple',
  'Deal Source':     'badge-teal',
  'Business Development': 'badge-orange',
  'Service Provider':'badge-gray',
  'RGA Team':        'badge-yellow',
  'Former RGA Team': 'badge-gray',
}

const TAG_COLORS = ['badge-blue','badge-green','badge-purple','badge-yellow','badge-red','badge-gray']
export function tagColor(t) { return TAG_COLORS[Math.abs([...t].reduce((a,c) => a + c.charCodeAt(0), 0)) % TAG_COLORS.length] }

export function formatDate(iso) {
  if (!iso) return null
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// After any change to a person: every list that shows people, the tag
// suggestions, the owner list (RGA Partners can own relationships) and the
// person's profile. Without an id (merge, bulk delete), every profile.
export function invalidatePeople(qc, id) {
  // universal-search: cached search results, so a new or renamed person is
  // findable right away.
  for (const key of [['contacts'], ['partners'], ['contactTags'], ['relationship-owners'], ['universal-search'],
    id ? ['contactProfile', id] : ['contactProfile']]) {
    qc.invalidateQueries({ queryKey: key })
  }
}

export function todayStr() { return new Date().toISOString().slice(0, 10) }

export function Modal({ title, onClose, width = 560, zIndex = 1000, children }) {
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 10, width: '100%', maxWidth: width,
        maxHeight: '92vh', overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      }}>
        {/* No title: the content supplies its own header and close button */}
        {title && (
          <div style={{
            padding: '16px 24px', borderBottom: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1,
          }}>
            <h2 style={{ fontSize: 15, fontWeight: 600 }}>{title}</h2>
            <button className="btn-icon" onClick={onClose} style={{ fontSize: 16 }}>✕</button>
          </div>
        )}
        {children}
      </div>
    </div>
  )
}

export function ErrorBanner({ msg }) {
  if (!msg) return null
  return (
    <div style={{
      background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
      borderRadius: 6, padding: '10px 14px', fontSize: 13, color: 'var(--danger-text)',
    }}>{msg}</div>
  )
}

export function TrashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
    </svg>
  )
}

export function DeleteConfirmModal({ name, onCancel, onConfirm, deleting, error }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1200, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 10, width: '100%', maxWidth: 420,
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)', padding: '24px',
      }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Delete {name}?</h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>
          Are you sure you want to delete <strong style={{ color: 'var(--text-primary)' }}>{name}</strong>?
          This action cannot be undone.
        </p>
        {error && <div style={{ marginBottom: 16 }}><ErrorBanner msg={error} /></div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="btn btn-secondary" onClick={onCancel} disabled={deleting}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  )
}

// A labeled value; empty values and empty lists show "—".
export function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>
        {(Array.isArray(children) ? children.length : children) ? children : '—'}
      </div>
    </div>
  )
}

export function SortHeader({ col, sortCol, sortDir, onSort, children }) {
  const active = sortCol === col
  return (
    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      onClick={() => onSort(col)}>
      {children} {active ? (sortDir === 'asc' ? '▲' : '▼') : <span style={{ opacity: 0.25 }}>▲</span>}
    </th>
  )
}

// Dropdown of checkboxes. Options render as badges: colored by badgeMap when
// given (e.g. contact types), otherwise by tagColor.
export function MultiSelectFilter({ label, options, selected, onToggle, badgeMap }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ position: 'relative' }}>
      <button className="btn btn-secondary btn-sm"
        onClick={() => setOpen(o => !o)}
        style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
        {label} {selected.length > 0 && (
          <span style={{
            background: 'var(--accent)', color: '#fff', borderRadius: 8,
            padding: '0 5px', fontSize: 10, fontWeight: 700, lineHeight: '16px',
          }}>{selected.length}</span>
        )}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 10,
          background: 'var(--surface)', border: '1px solid var(--border-light)',
          borderRadius: 6, marginTop: 4, minWidth: 180, maxHeight: 260, overflow: 'auto',
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}
          onMouseLeave={() => setOpen(false)}>
          {options.map(o => {
            const checked = selected.includes(o)
            return (
              <label key={o} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
                cursor: 'pointer', fontSize: 12,
                background: checked ? 'var(--accent-muted)' : 'transparent',
              }}>
                <input type="checkbox" checked={checked} onChange={() => onToggle(o)}
                  style={{ accentColor: 'var(--accent)' }} />
                {badgeMap
                  ? <span className={`badge ${badgeMap[o] ?? 'badge-gray'}`} style={{ fontSize: 10 }}>{o}</span>
                  : <span className={`badge ${tagColor(o)}`} style={{ fontSize: 10 }}>{o}</span>
                }
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}

export function TagInput({ tags, onChange, suggestions = [] }) {
  const [input, setInput] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)

  const filteredSuggestions = useMemo(() => {
    if (!input.trim()) return []
    const q = input.toLowerCase()
    return suggestions
      .filter(s => s.toLowerCase().includes(q) && !tags.includes(s))
      .slice(0, 8)
  }, [input, suggestions, tags])

  function addTag(t) {
    const tag = t.trim()
    if (tag && !tags.includes(tag)) onChange([...tags, tag])
    setInput('')
    setShowSuggestions(false)
  }

  function removeTag(t) { onChange(tags.filter(x => x !== t)) }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && input.trim()) {
      e.preventDefault()
      addTag(input)
    }
    if (e.key === 'Backspace' && !input && tags.length) {
      removeTag(tags[tags.length - 1])
    }
  }

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <input
          className="input"
          value={input}
          onChange={e => { setInput(e.target.value); setShowSuggestions(true) }}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder={tags.length ? 'Add another tag…' : 'Type a tag and press Enter'}
        />
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
            background: 'var(--surface)', border: '1px solid var(--border-light)',
            borderRadius: 6, marginTop: 2, maxHeight: 180, overflow: 'auto',
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          }}>
            {filteredSuggestions.map(s => (
              <div key={s}
                onMouseDown={() => addTag(s)}
                style={{
                  padding: '7px 12px', fontSize: 12, cursor: 'pointer',
                  color: 'var(--text-primary)',
                }}
                onMouseEnter={e => e.target.style.background = 'var(--accent-muted)'}
                onMouseLeave={e => e.target.style.background = 'transparent'}>
                {s}
              </div>
            ))}
          </div>
        )}
      </div>
      {tags.length > 0 && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 6 }}>
          {tags.map(t => (
            <span key={t} className={`badge ${tagColor(t)}`} style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {t}
              <span onClick={() => removeTag(t)}
                style={{ cursor: 'pointer', opacity: 0.7, fontWeight: 700, fontSize: 13, lineHeight: 1 }}>
                ×
              </span>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
